// @ts-strict-ignore
import { EventDispatcher } from 'three';
import { ArticleNode } from '@project/components/article/presenter/article-node';
import { MetaElementUsageIdentityVO } from '../vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '../util/array-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { ProjectContentTreeUtils } from '@project/components/project/util/project-content-tree-utils';
import { UserModel } from '@user/model/user.model';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { UserUtils } from '@user/service/user-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { StringUtils } from '@shared/util/string-utils';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { LocaleResourceManager } from '../manager/locale-resource.manager';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { Log } from '@shared/debug/log';
import { filter } from 'rxjs/operators';
import { LoadProjectInfoService } from '@project/service/load-project-info.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticlesMovedHandlerData } from '@project/components/article/manager/article-move.manager';
import { ProjectItemListHelper } from '@project/helper/project-item-list-helper';
import { IsProjectType } from '@project/model/is-project-type';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class ProjectContentPresenter extends EventDispatcher<Record<string, { articleUsageIds: string[] }>> implements OnDestroy {
  public static readonly LABEL_POSITION_SEPARATOR = '.';

  public project: ProjectVO;
  public _articleNodeList: ArticleNode[];
  public articleNodeListChanged: EventEmitter<any> = new EventEmitter<any>();
  public selectedNodes: EventEmitter<ArticleNode[]> = new EventEmitter<ArticleNode[]>();
  private _currentlyPresentedProject: ProjectVO = null;
  private _updateFunctionQueue: Function[] = [];
  public nodeIdToIsNodeExpanded: Map<string, boolean>;
  private _processPartsOfCompositeArticlesSeparately: boolean = false;

  private _articleVOReplacedSubscription: Subscription;
  private projectTreeRebuildedSubject: Subject<void> = new Subject<void>();

  public get projectTreeRebuilded$(): Observable<void> {
    return this.projectTreeRebuildedSubject.asObservable();
  }

  private _showCustomArticleNumber$ = new BehaviorSubject<boolean>(null);
  public get showCustomArticleNumber$() {
    return this._showCustomArticleNumber$.asObservable();
  }

  private _articlesLoadedSubscription: Subscription;

  private getWireBridgeGroupId(articleUsageId: string): string {
    // Wire-Pairs have a common groupId, but are no compositeArticle.
    const usage = this.projectModel.getArticleUsage(articleUsageId);
    const isCompositeArticle = ArticleUsageUtils.isCompositePartArticleUsage(usage);
    return isCompositeArticle ? null : usage?.groupId;
  }

  public findArticleNodesByArticleUsageId(
    articleUsageId: string,
    articleNodes: ArticleNode[],
    createNonPermanentNodes: boolean = false
  ): ArticleNode[] {
    // If a wire bridge is selected, we must find nodes with matching groupIds.
    const wireBridgeGroupId: string = this.getWireBridgeGroupId(articleUsageId);

    let result: ArticleNode[] = [];
    for (const articleNode of articleNodes) {
      const usageId: string = articleNode.effectiveArticleUsageId;

      const isMatch = wireBridgeGroupId
        ? wireBridgeGroupId === articleNode.articleUsage?.groupId
        : usageId === articleUsageId;

      if (isMatch) {
        result.push(articleNode);
      } else if (articleNode.drillingHoleUsageIds.indexOf(articleUsageId) > -1) {
        // if articleUsageId is included as drilling hole in articleNode
        // then look for accessories-node in the given articleNode
        const accessoriesNode: ArticleNode = this.findAccessoriesNode(articleNode, createNonPermanentNodes);
        if (!accessoriesNode) {
          const drillingHolesNode: ArticleNode = ProjectContentTreeUtils.createDrillingHolesNode(this.containerService);
          drillingHolesNode.drillingHoleUsages = [articleNode.findDrillingHoleUsageByUsageId(articleUsageId)];
          result.push(drillingHolesNode);
        } else {
          accessoriesNode.drillingHoleUsages = [articleNode.findDrillingHoleUsageByUsageId(articleUsageId)];
          result.push(accessoriesNode);
        }
      } else if (articleNode.children) {
        const nodes: ArticleNode[] = this.findArticleNodesByArticleUsageId(articleUsageId, articleNode.children);
        result = result.concat(nodes);
      }
    }
    return result;
  }

  private findParentArticleNodesByArticleUsageId(
    parentArticleNodeList: ArticleNode[],
    parentArticleUsageId: string,
    articleUsageId: string,
    createNonPermanentNodes: boolean
  ): ArticleNode[] {
    const parentNodes: ArticleNode[] = this.findArticleNodesByArticleUsageId(parentArticleUsageId, parentArticleNodeList, createNonPermanentNodes);
    const targetParentNodes: ArticleNode[] = [];
    if (parentNodes.length > 0) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      const parentContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentArticleUsageId);
      const isMountingRailParentContainer: boolean = !parentContainer ? false : ArticleUtils.isMountingRail(parentContainer.article);
      for (const parentNode of parentNodes) {
        let nodeToStore: ArticleNode = parentNode;
        let accessoriesNode: ArticleNode = null;
        if (isMountingRailParentContainer && this.articleContainerUtils.isMountingRailAccessoryArticle(articleContainer, parentContainer)) {
          accessoriesNode = this.findAccessoriesNode(parentNode, createNonPermanentNodes);
        }
        if (accessoriesNode) {
          nodeToStore = accessoriesNode;
        }
        targetParentNodes.push(nodeToStore);
      }
    }
    return targetParentNodes;
  }

  private findAccessoriesNode(parentNode: ArticleNode, createIfNotExists: boolean): ArticleNode {
    if (!parentNode) {
      return null;
    }
    let accessoriesNode: ArticleNode = ProjectContentTreeUtils.findAccessoriesNode(parentNode.children);
    if (accessoriesNode == null && createIfNotExists) {
      // create accessories-node if it does not exist
      accessoriesNode = ProjectContentTreeUtils.createAccessoriesNode(this.containerService, parentNode);
      if (!parentNode.children) {
        parentNode.children = [];
      }
      parentNode.children.splice(0, 0, accessoriesNode);
    }
    return accessoriesNode;
  }

  private executeRemoveArticleNodesByArticleUsageId(
    articleNodeList: ArticleNode[],
    articleUsageId: string,
    parentNode: ArticleNode = null,
    parentOfParentNode: ArticleNode = null
  ): void {
    if (!articleNodeList) {
      return;
    }
    for (let i: number = articleNodeList.length - 1; i > -1; i--) {
      const articleNode: ArticleNode = articleNodeList[i] as ArticleNode;
      if (articleNode) {
        if (articleNode.articleUsageId === articleUsageId) {
          articleNodeList.splice(i, 1);
          // if last item was removed from accessories-node - remove this node too
          if (articleNodeList.length === 0 && parentNode && parentOfParentNode && ProjectContentTreeUtils.isAccessoriesNode(parentNode)) {
            parentOfParentNode.children.splice(parentOfParentNode.children.indexOf(parentNode), 1);
          }
        } else {
          this.executeRemoveArticleNodesByArticleUsageId(articleNode.children, articleUsageId, articleNode, parentNode);
        }
      }
    }
  }

  private executeRemoveArticleNodesFromParentByArticleUsageId(
    articleNodeList: ArticleNode[],
    parentArticleUsageId: string,
    articleUsageId: string
  ): void {
    const parentNodes: ArticleNode[] = this.findParentArticleNodesByArticleUsageId(articleNodeList, parentArticleUsageId, articleUsageId, false);
    for (const parentNode of parentNodes) {
      if (parentNode) {
        this.executeRemoveArticleNodesByArticleUsageId(parentNode.children, articleUsageId);
      }
    }
  }
  public get articleNodeList(): ArticleNode[] {
    return this._articleNodeList;
  }
  public set articleNodeList(list: ArticleNode[]) {
    this._articleNodeList = list;
    this.articleNodeListChanged.emit();
  }

  public get showCustomArticleNumber(): boolean {
    return this._showCustomArticleNumber$.value;
  }

  public set showCustomArticleNumber(value: boolean) {
    this._showCustomArticleNumber$.next(value);
  }

  constructor(
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private selectionModel: SelectionModel,
    private projectHelper: ProjectHelper,
    private loadProjectInfoService: LoadProjectInfoService,
    private containerService: ContainerService,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private _insertArticleService: InsertArticleService,
    private localeResourceManager: LocaleResourceManager,
    private articleUsageService: ArticleUsageService,
    private projectItemListHelper: ProjectItemListHelper,
  ) {
    super();
    this.subscribeArticleSelectionChanged();
    this.init();
    loadProjectInfoService.projectLoaded$.subscribe(() => this.rebuildProjectTree());
  }

  public init() {
    this.nodeIdToIsNodeExpanded = new Map<string, boolean>();
    this.project = this.projectModel.currentProject;
    this.rebuildProjectTree(false);
    this.subscribeOnArticlesLoaded()
  }

  ngOnDestroy(): void {
    this._articlesLoadedSubscription?.unsubscribe();
    this._articlesLoadedSubscription = undefined;
    this._articleVOReplacedSubscription?.unsubscribe();
    this._articleVOReplacedSubscription = undefined;
  }

  public projectChangeHandler(): void {
    this.nodeIdToIsNodeExpanded.clear();
    let currentProject: ProjectVO = null;
    if (this.projectModel) {
      currentProject = this.projectModel.currentProject;
    }
    let doRebuildProjectTree: boolean = false;
    if (ProjectUtils.getBaseArticleNumber(currentProject) !== ProjectUtils.getBaseArticleNumber(this.project)) {
      doRebuildProjectTree = true;
    }
    if (currentProject !== this.project) {
      this.project = currentProject;
      doRebuildProjectTree = true;
    }
    if (ProjectUtils.isEmptyProject(this.project)) {
      doRebuildProjectTree = true;
    }
    if (!this.loadProjectInfoService.isLoading && doRebuildProjectTree) {
      this.rebuildProjectTree();
    }
  }

  private subscribeOnArticlesLoaded(): void {
    this._articlesLoadedSubscription = this._insertArticleService.allArticlesLoaded$.subscribe({
      next: () => this.rebuildProjectTree(),
      error: (error) => Log.error('Error in articlesLoadedNotification: ', error)
    });
  }

  public articleContainerAttachedHandler(data: any): void {
    if (this.loadProjectInfoService.isLoading) {
      return;
    }
    if (this.articleNodeList?.length === 0) {
      this.rebuildProjectTree(true);
      return;
    }

    const attachedContainer: ArticleContainer = data.container;
    const connectedTo: MetaElementUsageIdentityVO[] = data.connectedTo;
    // if attached article has "relativeParentUsageId" info property then add it to this relative parent
    // otherwise, as usual, add it to parent connected article (data.connectedTo)
    let parentUsageIdsToConnect: string[] = [];
    const relativeParentUsageId: string = ArticleUsageUtils.getRelativeParentUsageId(attachedContainer.articleUsage);

    if (relativeParentUsageId) {
      parentUsageIdsToConnect = [relativeParentUsageId];
    } else {
      for (const metaElementUsageIdentity of connectedTo) {
        if (!metaElementUsageIdentity) {
          continue;
        }
        this.attachMetaElementUsage(parentUsageIdsToConnect, metaElementUsageIdentity);
      }
    }

    this.internalArticleAttachedHandler(data.container, parentUsageIdsToConnect);
  }

  private attachMetaElementUsage(parentUsageIdsToConnect: string[], metaElementUsageIdentity: MetaElementUsageIdentityVO) {
    if (parentUsageIdsToConnect.indexOf(metaElementUsageIdentity.articleUsageId) < 0) {
      const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaElementUsageIdentity.articleUsageId);
      // if container is attached to part of composite article, we need to use the composite article usage id
      if (this.compositeArticleInfoUtils.isCompositePartArticle(container) && !this._processPartsOfCompositeArticlesSeparately) {
        parentUsageIdsToConnect.push(CompositeArticleInfoUtils.getCompositeArticleUsageId(container));
      } else {
        parentUsageIdsToConnect.push(metaElementUsageIdentity.articleUsageId);
      }
    }
  }

  public articleDetachedHandler(container: ArticleContainer): void {
    this.removeArticleNodesByArticleUsageId(container.articleGUID);
  }

  private removeArticleNodesByArticleUsageId(articleUsageId: string): void {
    this._updateFunctionQueue.push((articleNodeList: ArticleNode[]) => {
      this.executeRemoveArticleNodesByArticleUsageId(articleNodeList, articleUsageId);
    });
    this.executeRefresh();
  }

  private isAddedAsCompositeArticle(articleUsage: ArticleUsageVO, parentUsageId: string): boolean {
    if (!articleUsage) {
      return false;
    }
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    if (!this.compositeArticleInfoUtils.isCompositePartArticle(container)) {
      return false;
    }
    const compositeUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
    return compositeUsageId === parentUsageId;
  }

  public rebuildProjectTree(rebuildOnlyRootItems: boolean = false): void {
    if (this.loadProjectInfoService.isLoading) {
      return;
    }
    this._updateFunctionQueue = [];
    const newArticleNodeList = this.createArticleNodeList(this.project, rebuildOnlyRootItems);
    this._currentlyPresentedProject = this.project;
    this.executeRefresh(false, newArticleNodeList);
    this.projectTreeRebuildedSubject.next();
  }

  private createArticleNodeList(project: ProjectVO, createOnlyRootNodes: boolean): ArticleNode[] {
    const rootNodes: ArticleNode[] = this.projectItemListHelper.createRootArticleNodes(project);
    const nodeList: ArticleNode[] = rootNodes.slice(0);
    // articleNodeList = new ArrayCollection(rootNodes);
    if (!createOnlyRootNodes) {
      for (const rootArticleNode of rootNodes) {
        this.buildArticleNodeList(rootArticleNode.articleUsage, rootArticleNode, project);
      }
    }
    if (
      ArrayUtils.isNotEmpty(nodeList) &&
      this.projectModel !== null &&
      IsProjectType.mcsGds(this.projectModel.currentProject) &&
      ArticleUtils.isFreeMounting(nodeList[0].article)
    ) {
      const baseArticle: ArticleVO = this.projectModel.baseArticle;
      if (baseArticle) {
        nodeList[0].article = baseArticle;
      }
    }
    return nodeList;
  }

  private buildArticleNodeList(
    parentArticleUsage: ArticleUsageVO,
    parentNode: ArticleNode,
    project: ProjectVO,
    destinationNode: ArticleNode = null
  ): void {
    if (!parentNode.children) {
      parentNode.children = [];
    }
    const isMountingRailParentNode: boolean = ArticleUtils.isMountingRail(parentNode.article);
    let mountingRailParentContainer: ArticleContainer = null;
    if (isMountingRailParentNode) {
      mountingRailParentContainer = this.containerService.findArticleContainerInGlobal(parentArticleUsage.id);
    }

    // create accessories node for accessories of mounting rail (which are connected to non-freedom connection on rail or connected
    // to drilling holes)
    const accessoriesNode: ArticleNode = ProjectContentTreeUtils.createAccessoriesNode(this.containerService, parentNode);

    const childUsageIdsToAttach: string[] = ArticleUsageUtils.getAcceptingAreaIds(parentArticleUsage);
    const relativeChildUsageIds: string[] = this.projectArticleService.collectRelativeArticleUsageIdsByParentUsage(parentArticleUsage);
    ArrayUtils.mergeArrays(childUsageIdsToAttach, relativeChildUsageIds);
    for (const childUsageId of childUsageIdsToAttach) {
      const articleUsage: ArticleUsageVO = ProjectUtils.findArticleUsageInProjectById(project, childUsageId);
      if (!articleUsage) {
        continue;
      }
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
      if (!articleContainer) {
        continue;
      }
      // if article is a drilling hole
      if (ArticleUtils.isDrillingHole(articleContainer.article)) {
        // add its usage id to parent list of drilling holes usage ids
        parentNode.drillingHoleUsageIds.push(childUsageId);
        parentNode.drillingHoleUsages.push(articleUsage);
        // create node for drilling hole
        const tempDrillingHoleNode: ArticleNode = this.createArticleNode(articleUsage, parentNode);
        // call buildArticleNodeList recursively for drilling-hole as a parent node
        // children of the drilling hole will be added to accessories-node in this case
        this.buildArticleNodeList(tempDrillingHoleNode.articleUsage, tempDrillingHoleNode, project, accessoriesNode);
      } else {
        const relativeParentId: string = ArticleUsageUtils.getRelativeParentUsageId(articleUsage);
        // add relative child articles that have the same "relativeParentUsageId" property as the article usage id of the given parent node
        // and thus ignore directly connected articles that have a different "relativeParentUsageId"
        if (!relativeParentId || relativeParentId === parentNode.articleUsageId) {
          // add child node to parentNode if destinationNode is not specified
          let targetNode: ArticleNode = destinationNode ? destinationNode : parentNode;
          if (
            mountingRailParentContainer &&
            this.articleContainerUtils.isMountingRailAccessoryArticle(articleContainer, mountingRailParentContainer)
          ) {
            targetNode = accessoriesNode;
          }
          if (this._processPartsOfCompositeArticlesSeparately) {
            const childNode: ArticleNode = this.addArticleNodeToParent(targetNode, articleUsage);
            this.buildArticleNodeList(childNode.articleUsage, childNode, project);
          } else {
            const parentCompositeArticleUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(targetNode.articleUsage);
            const childCompositeArticleUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleUsage);
            if (!parentCompositeArticleUsageId || parentCompositeArticleUsageId !== childCompositeArticleUsageId) {
              const childNode: ArticleNode = this.addArticleNodeToParent(targetNode, articleUsage);
              this.buildArticleNodeList(childNode.articleUsage, childNode, project);
            } else {
              this.buildArticleNodeList(articleUsage, targetNode, project, targetNode);
            }
          }
        }
      }
    }
    if (accessoriesNode.children && accessoriesNode.children.length > 0) {
      // add accessories node to the parentNode if it's not empty
      parentNode.children.splice(0, 0, accessoriesNode);
    }
  }

  private executeRefresh(updateSelection: boolean = false, newArticleNodeList: ArticleNode[] = null): void {
    if (this.loadProjectInfoService.isLoading) {
      return;
    }
    const currentProject: ProjectVO = this.project;
    let articleTreeWasChanged: boolean = false;

    if (!newArticleNodeList) {
      if (currentProject !== this._currentlyPresentedProject || !this.articleNodeList || this.articleNodeList.length === 0) {
        newArticleNodeList = this.projectItemListHelper.createRootArticleNodes(currentProject);
        this._currentlyPresentedProject = currentProject;
        articleTreeWasChanged = true;
      }
      if (this._updateFunctionQueue && this._updateFunctionQueue.length > 0) {
        if (!newArticleNodeList) {
          newArticleNodeList = this.articleNodeList;
        }
        this._updateFunctionQueue.forEach((updateFunction: Function) => {
          updateFunction(newArticleNodeList);
        });
        this._updateFunctionQueue = [];
        articleTreeWasChanged = true;
      }
    }
    if (newArticleNodeList && newArticleNodeList !== this.articleNodeList) {
      this.setArticleNodeList(currentProject, newArticleNodeList);
      articleTreeWasChanged = true;
    }
    if (updateSelection) {
      this.executeUpdateArticleSelection(this.articleNodeList);
    }
    if (articleTreeWasChanged) {
      this.refreshPositionLabels();
      this.articleNodeListChanged.emit();
    }
  }

  private setArticleNodeList(currentProject: ProjectVO, newArticleNodeList: ArticleNode[]) {
    if (IsProjectType.xCom(currentProject)) {
      const rootArticleNode: ArticleNode = new ArticleNode(this.containerService);
      rootArticleNode.children = newArticleNodeList;
      this.articleNodeList = [rootArticleNode];
    } else {
      this.articleNodeList = newArticleNodeList;
    }
  }

  private createArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode): ArticleNode {
    if (!this._processPartsOfCompositeArticlesSeparately && ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
      return this.createCompositeArticleNode(articleUsage, parentNode);
    } else {
      return this.createDefaultArticleNode(articleUsage, parentNode);
    }
  }

  private createDefaultArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode): ArticleNode {
    return new ArticleNode(
      this.containerService,
      articleUsage.id,
      parentNode,
      this.articleUsageService.getArticleByArticleUsage(articleUsage),
      articleUsage,
      this.articleUsageService.isArticleUsageDeletable(articleUsage) &&
        ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.userModel.user),
      false,
      false
    );
  }

  private createCompositeArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode): ArticleNode {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(container);
    return new ArticleNode(
      this.containerService,
      CompositeArticleInfoUtils.getCompositeArticleUsageId(container),
      parentNode,
      compositeArticle,
      articleUsage,
      ArticleUtils.isArticleDeletable(compositeArticle) &&
        ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.userModel.user),
      true,
      this.projectHelper.isCompositePartArticleSelectionAllowed(container)
    );
  }

  public metaElementAttachedHandler(data: any): void {
    this.addArticleNodeToParentByArticleUsageId(data.connectedTo.articleUsageId, data.identity.articleUsageId);
  }

  private internalArticleAttachedHandler(container: ArticleContainer, parentUsageIdsToConnect: string[]): void {
    if (!parentUsageIdsToConnect) {
      return;
    }
    for (let i: number = 0; i < parentUsageIdsToConnect.length; i++) {
      this.addArticleNodeToParentByArticleUsageId(parentUsageIdsToConnect[i], container.articleGUID);
    }
  }

  private addArticleNodeToParentByArticleUsageId(parentArticleUsageId: string, articleUsageId: string): void {
    // get article usage right here, because in the delayed execute method, the articleUsage might already have been deleted
    const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageId);
    if (this.articleUsageService.isDrillingHoleArticleUsage(articleUsage)) {
      this.addDrillingHoleUsageIdToParentNode(parentArticleUsageId, articleUsageId, articleUsage, this.articleNodeList);
    } else {
      this._updateFunctionQueue.push((articleNodeList: ArticleNode[]) => {
        this.executeAddArticleNodeToParentByArticleUsageId(articleNodeList, parentArticleUsageId, articleUsage);
      });
      this.executeRefresh(true);
    }
  }

  private addDrillingHoleUsageIdToParentNode(
    parentArticleUsageId: string,
    drillingHoleUsageId: string,
    drillingHoleUsage: ArticleUsageVO,
    articleList: ArticleNode[]
  ): boolean {
    let usageIdAdded: boolean = false;
    for (const articleNode of articleList) {
      if (articleNode.articleUsageId === parentArticleUsageId) {
        articleNode.drillingHoleUsageIds.push(drillingHoleUsageId);
        articleNode.drillingHoleUsages.push(drillingHoleUsage);
        usageIdAdded = true;
        break;
      } else if (articleNode.children) {
        usageIdAdded = this.addDrillingHoleUsageIdToParentNode(parentArticleUsageId, drillingHoleUsageId, drillingHoleUsage, articleNode.children);
        if (usageIdAdded) {
          break;
        }
      }
    }
    return usageIdAdded;
  }

  private executeAddArticleNodeToParentByArticleUsageId(
    articleNodeList: ArticleNode[],
    parentArticleUsageId: string,
    articleUsage: ArticleUsageVO
  ): void {
    if (!articleUsage) {
      return;
    }
    // ignore article if it's part of already added composite article
    if (!this._processPartsOfCompositeArticlesSeparately && this.isAddedAsCompositeArticle(articleUsage, parentArticleUsageId)) {
      return;
    }
    // parentNodes: ArticleNodes which are backwards connected to articleUsage.
    const backwardArticleUsageIds: string[] = ArticleUsageUtils.backwardConnectionAreaUsages(articleUsage)
      .map(usage => usage.connection.acceptingAreaId.articleUsageId);
    const backwardCombinedArticleUsageIds: string[] = backwardArticleUsageIds
      .flatMap((articleUsageId) => {
        const articleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
        const combinedArticles = this.articleContainerUtils.findCombinedArticles(articleContainer);
        return combinedArticles.map((combinedArticle) => combinedArticle.articleUsage.id);
      });

    const parentNodes: ArticleNode[] = this.findParentArticleNodesByArticleUsageId(articleNodeList, parentArticleUsageId, articleUsage.id, true)
        .filter(node =>
          // Accessory should only be shown below special node and if it's directly connected to the parent node.
          // In addition, if the parent node is a combined article, it needs to be shown, too.
          node.id === ProjectContentTreeUtils.ACCESSORIES_NODE_ID
          || backwardArticleUsageIds.includes(node.articleUsage?.id)
          || backwardCombinedArticleUsageIds.includes(node.articleUsage?.id));

    const createdNodes: ArticleNode[] = [];
    for (const parentNode of parentNodes) {
      const childNode: ArticleNode = this.findFirstArticleNodeByArticleUsageId(parentNode.children, articleUsage.id);
      if (!childNode) {
        const newNode: ArticleNode = this.addArticleNodeToParent(parentNode, articleUsage);
        if (newNode) {
          createdNodes.push(newNode);
        }
      }
    }
    for (const createdNode of createdNodes) {
      if (!createdNode.children || createdNode.children.length === 0) {
        this.buildArticleNodeList(createdNode.articleUsage, createdNode, this.projectModel.currentProject);
      }
    }
  }

  private addArticleNodeToParent(parentNode: ArticleNode, articleUsage: ArticleUsageVO): ArticleNode {
    if (!parentNode || !articleUsage || ArticleUsageUtils.isArticleUsageWithout3DModel(articleUsage)) {
      return null;
    }
    let parentNodeChildren: ArticleNode[] = parentNode.children;
    if (!parentNodeChildren) {
      parentNodeChildren = [];
    }
    const newArticleNode: ArticleNode = this.createArticleNode(articleUsage, parentNode);
    let i: number;
    for (i = parentNodeChildren.length - 1; i > -1; i--) {
      const child: ArticleNode = parentNodeChildren[i] as ArticleNode;
      if (ProjectContentTreeUtils.isAccessoriesNode(child) || newArticleNode.distanceToRoot >= child.distanceToRoot) {
        break;
      }
    }
    parentNodeChildren.splice(i + 1, 0, newArticleNode);
    parentNode.children = parentNodeChildren;
    return newArticleNode;
  }

  public removeArticleNodesFromParentByArticleUsageId(parentArticleUsageId: string, articleUsageId: string): void {
    this._updateFunctionQueue.push((articleNodeList: ArticleNode[]): void => {
      this.executeRemoveArticleNodesFromParentByArticleUsageId(articleNodeList, parentArticleUsageId, articleUsageId);
    });
    this.executeRefresh();
  }

  private executeUpdateArticleSelection(articleNodeList: ArticleNode[]): void {
    const selectedNodes: ArticleNode[] = [];
    const selectedArticleUsageIds: any[] = !this.selectionModel ? null : this.selectionModel.getArticleUsageIds();
    if (selectedArticleUsageIds && selectedArticleUsageIds.length > 0) {
      for (const selectedArticleUsageId of selectedArticleUsageIds) {
        const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(selectedArticleUsageId);
        let articleUsageId: string = selectedArticleUsageId;
        if (!this._processPartsOfCompositeArticlesSeparately && container && this.compositeArticleInfoUtils.isCompositePartArticle(container)) {
          articleUsageId = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
        }
        const foundNodes: ArticleNode[] = this.findArticleNodesByArticleUsageId(articleUsageId, articleNodeList);
        if (foundNodes.length > 0) {
          ArrayUtils.mergeArrays(selectedNodes, foundNodes);
        }
      }
    }
    this.selectedNodes.emit(selectedNodes);
  }

  private findFirstArticleNodeByArticleUsageId(articleNodeList: ArticleNode[], articleUsageId: string): ArticleNode {
    let result: ArticleNode = null;
    if (!articleNodeList) {
      return result;
    }
    for (const articleNode of articleNodeList) {
      if (articleNode) {
        if (articleNode.articleUsageId === articleUsageId) {
          result = articleNode;
        } else {
          result = this.findFirstArticleNodeByArticleUsageId(articleNode.children, articleUsageId);
        }
      }
      if (result) {
        break;
      }
    }
    return result;
  }

  public refreshPositionLabels(): void {
    let i: number = 0;
    while (this.articleNodeList && i < this.articleNodeList.length) {
      this.updatePositionLabel(this.articleNodeList[i] as ArticleNode, i, '');
      i++;
    }
  }

  private updatePositionLabel(node: ArticleNode, pos: number, parentlabel: string): void {
    if (node) {
      let label: string = parentlabel;
      if (pos > 0) {
        if (label.length > 0) {
          label = label + ProjectContentPresenter.LABEL_POSITION_SEPARATOR;
        }
        label = label + pos.toString();
      }
      node.positionLabel = label;
      if (node.children && node.children.length > 0) {
        let i: number = 0;
        let posCounter: number = 1;
        while (i < node.children.length) {
          const articleNode: ArticleNode = node.children[i] as ArticleNode;
          if (!ArticleUtils.isZoneArticle(articleNode.article) && !ProjectContentTreeUtils.isAccessoriesNode(articleNode)) {
            this.updatePositionLabel(articleNode, posCounter, label);
            posCounter++;
          }
          i++;
        }
      }
    }
  }

  public getPositionLabelByArticleUsageId(articleUsageId: string): string {
    const node: ArticleNode = this.findFirstArticleNodeByArticleUsageId(this.articleNodeList, articleUsageId);
    if (node) {
      return node.positionLabel;
    }
    return '';
  }

  public mcsGdsProjectCreated_success(): void {
    this.rebuildProjectTree();
  }

  public articleContainerMovedHandler(data: ArticlesMovedHandlerData) {
    if (data.articleOrderChanged) {
      if (data.articleUsageIds) {
        this.moveArticleNodeByArticleUsageId(data.articleUsageIds);
      } else {
        this.rebuildProjectTree();
      }
      this.executeRefresh(true);
    }
  }
  private moveArticleNodeByArticleUsageId(articleUsageIds: any[]) {
    this._updateFunctionQueue.push((articleNodeList: ArticleNode[]): void => {
      this.executeMoveArticleNodeByArticleUsageId(articleNodeList, articleUsageIds);
    });
  }

  private executeMoveArticleNodeByArticleUsageId(articleNodeList: ArticleNode[], articleUsageIds: any): void {
    const articleNodesWithNewPosition: ArticleNode[] = [];
    for (const articleUsageId of articleUsageIds) {
      const articleNode: ArticleNode = this.findFirstArticleNodeByArticleUsageId(articleNodeList, articleUsageId);
      if(Log.assertExists(articleNode)) {
        articleNodesWithNewPosition.push(articleNode);
      }
    }
    for (const articleNodeToDelete of articleNodesWithNewPosition) {
      const parentArticleNode: ArticleNode = articleNodeToDelete.parent;
      parentArticleNode.children.splice(parentArticleNode.children.indexOf(articleNodeToDelete), 1);
    }
    for (const articleNodeWithNewPosition of articleNodesWithNewPosition) {
      const parentArticleNode: ArticleNode = articleNodeWithNewPosition.parent;
      const newPosition: number = this.getNewPositionInParent(articleNodeWithNewPosition, parentArticleNode);
      parentArticleNode.children.splice(newPosition, 0, articleNodeWithNewPosition);
    }
  }

  private getNewPositionInParent(articleNode: ArticleNode, parentArticleNode: ArticleNode): number {
    let position: number = -1;
    const articleDistanceToRoot: number = articleNode.distanceToRoot;
    let startIndex: number = 0;
    let endIndex: number = parentArticleNode.children.length - 1;
    while (position === -1) {
      if (endIndex - startIndex <= 1) {
        position = endIndex + 1;
        for (let index: number = startIndex; index <= endIndex; index++) {
          const currentArticleNode: ArticleNode = parentArticleNode.children[index] as ArticleNode;
          if (articleDistanceToRoot < currentArticleNode.distanceToRoot) {
            position = index;
            break;
          }
        }
      } else {
        const middleIndex: number = Math.floor((startIndex + endIndex) / 2);
        const middleArticleNode: ArticleNode = parentArticleNode.children[middleIndex] as ArticleNode;
        if (articleDistanceToRoot < middleArticleNode.distanceToRoot) {
          endIndex = middleIndex;
        } else {
          startIndex = middleIndex;
        }
      }
    }
    return position;
  }

  public articleReplaced(articleContainer: ArticleContainer): void {
    if (!articleContainer) {
      return;
    }
    const articleNodes: ArticleNode[] = this.findArticleNodesByArticleUsageId(articleContainer.articleGUID, this.articleNodeList);
    if (!articleNodes || articleNodes.length === 0) {
      return;
    }
    articleNodes.forEach((articleNode: ArticleNode) => {
      articleNode.article = articleContainer.article;
    });
    this.articleNodeListChanged.emit();
  }

  public isExpanded(id): boolean {
    return this.nodeIdToIsNodeExpanded.get(id) ? true : false;
  }

  public toggleList(id) {
    const isExpanded: boolean = this.nodeIdToIsNodeExpanded.get(id);
    this.nodeIdToIsNodeExpanded.set(id, !isExpanded);
  }

  public changeArticleVisibility(articleNode: ArticleNode, event: string): void {
    let usageIds: string[];
    if (articleNode.isArticleListNode && ArrayUtils.isNotEmpty(articleNode.articleListArticleUsageIds)) {
      usageIds = [];
      const addIds: string[] = [];
      articleNode.articleListArticleUsageIds.forEach((id) => {
        const groupedIds: string[] = this.projectArticleService.findArticleUsageIdsByGroupId(id);
        ArrayUtils.mergeArrays(addIds, ArrayUtils.isEmpty(groupedIds) ? [id] : groupedIds);
      });
      for (const usageId of addIds) {
        const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
        if (container) {
          const compositeIds: string[] = this.containerService.getCompositePartArticleUsageIds(container);
          while (ArrayUtils.isNotEmpty(compositeIds)) {
            const compositePartArticleUsageId: string = compositeIds.pop();
            if (usageIds.indexOf(compositePartArticleUsageId) < 0) {
              usageIds.push(compositePartArticleUsageId);
            }
          }
        }
        if (usageIds.indexOf(usageId) < 0) {
          usageIds.push(usageId);
        }
      }
    } else if (this.articleUsageService.isWireBridgeArticleUsage(articleNode.articleUsage)) {
      usageIds = this.projectArticleService.findArticleUsageIdsByGroupId(articleNode.articleUsage.groupId);
    } else if (articleNode.isComposite) {
      usageIds = this.getCompositePartsUsageIds(articleNode.articleUsage);
    } else {
      usageIds = [articleNode.articleUsageId];
    }
    this.dispatchEvent({ type: StringUtils.isNotEmpty(event) ? event : 'toggleArticleVisibility', articleUsageIds: usageIds });
  }

  private getCompositePartsUsageIds(usage: ArticleUsageVO): string[] {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(usage.id);
    return this.containerService.getCompositePartArticleUsageIds(container);
  }

  public isArticleNodeRowVisible(row: HTMLTableRowElement) {
    const rect = row.getBoundingClientRect();
    const vWidth = window.innerWidth || document.documentElement.clientWidth;
    const vHeight = window.innerHeight || document.documentElement.clientHeight;
    const efp = function (x, y) {
      return document.elementFromPoint(x, y);
    };

    // Return false if it's not in the viewport
    if (rect.right < 0 || rect.bottom < 0 || rect.left > vWidth || rect.top > vHeight) {
      return false;
    }
    return row.contains(efp((rect.left + rect.right) / 2, (rect.top + rect.bottom) / 2));
  }

  public shouldNodeShowRemarksNotification(node: ArticleNode, includeChildren: boolean = false, includeParent: boolean = true): boolean {
    const showInternalRemarks: boolean = UserUtils.canSeeInternalComments(this.projectModel.user);
    if (node instanceof ArticleNode) {
      const articleNode: ArticleNode = node;
      const articleUsage: ArticleUsageVO = articleNode.articleUsage;
      const internalRemark: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.REMARKS_INTERNAL
      );
      if (showInternalRemarks) {
        if (!StringUtils.isEmpty(internalRemark) && includeParent) {
          return true;
        }
      }
      const showExternalRemarks: boolean = UserUtils.canSeeExternalComments(this.projectModel.user, StringUtils.isEmpty(internalRemark));
      if (showExternalRemarks) {
        if (articleUsage && this.hasRemarksProperty(articleUsage.infoProperties) && includeParent) {
          return true;
        }
        if (includeChildren && ArrayUtils.isNotEmpty(articleNode.children)) {
          for (const childNode of articleNode.children) {
            if (this.shouldNodeShowRemarksNotification(childNode, includeChildren, true)) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  private hasRemarksProperty(properties: PropertyVO[]): boolean {
    if (ArrayUtils.isEmpty(properties)) {
      return null;
    }
    for (let i = 0; i < properties.length; i++) {
      const property: PropertyVO = properties[i];
      if (property && !StringUtils.isEmpty(property.name)) {
        for (const locale of this.localeResourceManager.getLanguages()) {
          if (
            property.name === ArticleUsageInfoPropertyDef.REMARKS + locale.langKey &&
            !StringUtils.isEmpty(PropertyUtils.getStringPropertyValueByProperty(property))
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private subscribeArticleSelectionChanged() {
    this.selectionModel.selectionChanged$.pipe(
      filter(data => data.some(sd => sd.selectionType === "article"))
    ).subscribe(() => this.executeRefresh(true));
  }
}
